import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import {
  PlusIcon,
  TrophyIcon,
  CheckCircleIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import { drawsAPI, batchesAPI } from '../services/api';
import {
  LoadingSpinner,
  EmptyState,
  StatCard,
  Badge,
  Modal,
  ConfirmDialog,
  DataTable,
  Pagination,
} from '../components/common';
import { formatDate, formatDateTime } from '../utils/formatters';

export default function DrawsPage() {
  const queryClient = useQueryClient();
  const { register, handleSubmit, reset, formState: { errors } } = useForm();

  const [isStartModalOpen, setIsStartModalOpen] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [isStartingAll, setIsStartingAll] = useState(false);

  // Fetch all draws
  const { data: allDrawsData, isLoading: allLoading } = useQuery({
    queryKey: ['all-draws', currentPage, pageSize],
    queryFn: async () => {
      const params = {
        page: currentPage,
        limit: pageSize,
      };
      const response = await drawsAPI.getAll(params);
      return response.data;
    },
  });

  const allDraws = allDrawsData?.data || [];
  const pagination = allDrawsData?.pagination || { total: 0, pages: 1 };

  // Fetch live draws with auto-refresh
  const { data: liveDrawsData, isLoading: liveLoading } = useQuery({
    queryKey: ['live-draws', currentPage, pageSize],
    queryFn: async () => {
      const params = {
        page: currentPage,
        limit: pageSize,
      };
      const response = await drawsAPI.getLive(params);
      return response.data;
    },
    refetchInterval: 5000,
  });

  const liveDraws = liveDrawsData?.data || [];

  // Fetch active batches for start draw
  const { data: batches } = useQuery({
    queryKey: ['batches-for-draw'],
    queryFn: async () => {
      const response = await batchesAPI.getAll();
      return Array.isArray(response.data) ? response.data : (response.data?.data || []);
    },
  });

  // Start draw mutation
  const startMutation = useMutation({
    mutationFn: (data) => drawsAPI.start(data),
    onSuccess: () => {
      queryClient.invalidateQueries(['live-draws']);
      queryClient.invalidateQueries(['all-draws']);
      toast.success('Draw started successfully!');
      setIsStartModalOpen(false);
      reset();
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to start draw');
    },
  });

  // Complete draw mutation
  const completeMutation = useMutation({
    mutationFn: (id) => drawsAPI.complete(id),
    onSuccess: () => {
      queryClient.invalidateQueries(['live-draws']);
      queryClient.invalidateQueries(['all-draws']);
      toast.success('Draw completed successfully!');
      setConfirmAction(null);
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to complete draw');
    },
  });

  // Cancel draw mutation
  const cancelMutation = useMutation({
    mutationFn: (id) => drawsAPI.cancel(id),
    onSuccess: () => {
      queryClient.invalidateQueries(['live-draws']);
      queryClient.invalidateQueries(['all-draws']);
      toast.success('Draw cancelled successfully!');
      setConfirmAction(null);
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to cancel draw');
    },
  });

  const onSubmit = (data) => {
    startMutation.mutate({
      batch_id: data.batch_id,
    });
  };

  const handleStartAllDraws = async () => {
    const activeBatches = batches?.filter(b => b.status === 'active') || [];
    
    if (activeBatches.length === 0) {
      toast.error('No active batches found');
      return;
    }

    // Show confirmation dialog
    setConfirmAction({
      type: 'startAll',
      batches: activeBatches,
      title: 'Start All Draws',
      message: `Are you sure you want to start draws for all ${activeBatches.length} active batches? This will initiate lottery draws for all active batches simultaneously.`,
    });
  };

  const handleConfirmStartAll = async () => {
    setIsStartingAll(true);
    
    try {
      setConfirmAction(null);
      const response = await drawsAPI.startAll();
      
      queryClient.invalidateQueries(['live-draws']);
      queryClient.invalidateQueries(['all-draws']);
      
      if (response.data.success) {
        toast.success(`Started ${response.data.total} draws successfully!`);
      } else {
        toast.warning(`Started ${response.data.successful} draws, ${response.data.failed} failed`);
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to start draws');
    } finally {
      setIsStartingAll(false);
      setConfirmAction(null);
    }
  };

  const handleComplete = (draw) => {
    setConfirmAction({
      type: 'complete',
      draw,
      title: 'Complete Draw',
      message: `Are you sure you want to complete Draw #${draw.id}? This will finalize all winners.`,
    });
  };

  const handleCancel = (draw) => {
    setConfirmAction({
      type: 'cancel',
      draw,
      title: 'Cancel Draw',
      message: `Are you sure you want to cancel Draw #${draw.id}? This action cannot be undone.`,
    });
  };

  const confirmActionHandler = () => {
    if (confirmAction.type === 'complete') {
      completeMutation.mutate(confirmAction.draw.id);
    } else if (confirmAction.type === 'cancel') {
      cancelMutation.mutate(confirmAction.draw.id);
    } else if (confirmAction.type === 'startAll') {
      handleConfirmStartAll();
    }
  };

  const columns = [
    {
      key: 'id',
      label: 'Draw ID',
      render: (value) => `#${value}`,
    },
    {
      key: 'batch_id',
      label: 'Batch',
      render: (value) => `Batch #${value}`,
    },
    {
      key: 'draw_date',
      label: 'Date',
      render: (value) => formatDateTime(value),
    },
    {
      key: 'total_candidates',
      label: 'Candidates',
    },
    {
      key: 'total_winners',
      label: 'Winners',
    },
    {
      key: 'status',
      label: 'Status',
      render: (value) => (
        <Badge variant={
          value === 'completed' ? 'success' :
          value === 'in_progress' ? 'info' :
          value === 'cancelled' ? 'danger' :
          'default'
        }>
          {value}
        </Badge>
      ),
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (_, draw) => (
        <div className="flex gap-2">
          {draw.status === 'in_progress' && (
            <>
              <button
                onClick={() => handleComplete(draw)}
                className="text-green-600 hover:text-green-800 text-sm font-medium"
              >
                Complete
              </button>
              <button
                onClick={() => handleCancel(draw)}
                className="text-red-600 hover:text-red-800 text-sm font-medium"
              >
                Cancel
              </button>
            </>
          )}
        </div>
      ),
    },
  ];

  if (liveLoading) {
    return <LoadingSpinner text="Loading draws..." />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Draws</h1>
          <p className="mt-2 text-sm text-gray-600">
            Manage lottery draws and monitor live draws
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => handleStartAllDraws()}
            disabled={isStartingAll || !batches?.some(b => b.status === 'active')}
            className="btn-secondary flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isStartingAll ? (
              <LoadingSpinner size="sm" />
            ) : (
              <PlusIcon className="h-5 w-5" />
            )}
            Start All
          </button>
          {/* <button
            onClick={() => setIsStartModalOpen(true)}
            className="btn-primary flex items-center gap-2"
          >
            <PlusIcon className="h-5 w-5" />
            Start New Draw
          </button> */}
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          title="Live Draws"
          value={liveDraws?.length || 0}
          icon={TrophyIcon}
          color="green"
        />
        <StatCard
          title="Total Draws"
          value={allDraws?.length || 0}
          icon={TrophyIcon}
          color="primary"
        />
        <StatCard
          title="Completed"
          value={allDraws?.filter(d => d.status === 'completed').length || 0}
          icon={CheckCircleIcon}
          color="blue"
        />
        <StatCard
          title="Cancelled"
          value={allDraws?.filter(d => d.status === 'cancelled').length || 0}
          icon={XCircleIcon}
          color="red"
        />
      </div>

      {/* Live Draws */}
      {liveDraws && liveDraws.length > 0 && (
        <div className="card">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Live Draws</h2>
          <div className="space-y-4">
            {liveDraws.map((draw) => (
              <div
                key={draw.id}
                className="p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border border-green-200"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="font-semibold text-gray-900">Draw #{draw.id}</p>
                      <span className="relative flex h-3 w-3">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                        <span className="relative inline-flex rounded-full h-3 w-3 bg-green-500"></span>
                      </span>
                      <Badge variant="success">Live</Badge>
                    </div>
                    <p className="text-sm text-gray-600 mt-1">
                      Batch #{draw.batch_id} • {draw.total_candidates} candidates
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleComplete(draw)}
                      className="btn-primary flex items-center gap-2"
                    >
                      <CheckCircleIcon className="h-4 w-4" />
                      Complete
                    </button>
                    <button
                      onClick={() => handleCancel(draw)}
                      className="btn-danger flex items-center gap-2"
                    >
                      <XCircleIcon className="h-4 w-4" />
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* All Draws Table */}
      <div className="card">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">All Draws</h2>
        {allDraws && allDraws.length > 0 ? (
          <>
            <DataTable
              columns={columns}
              data={allDraws}
            />
            <Pagination
              currentPage={currentPage}
              totalPages={pagination.pages}
              totalItems={pagination.total}
              itemsPerPage={pageSize}
              onPageChange={setCurrentPage}
              onPageSizeChange={setPageSize}
              isLoading={liveLoading}
            />
          </>
        ) : (
          <EmptyState
            icon={TrophyIcon}
            title="No draws yet"
            description="Start your first draw to get started"
            action={() => setIsStartModalOpen(true)}
            actionLabel="Start Draw"
          />
        )}
      </div>

      {/* Start Draw Modal */}
      <Modal
        isOpen={isStartModalOpen}
        onClose={() => {
          setIsStartModalOpen(false);
          reset();
        }}
        title="Start New Draw"
        size="md"
      >
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Select Batch *
            </label>
            <select
              {...register('batch_id', { required: 'Batch is required' })}
              className="input-field"
            >
              <option value="">Select batch...</option>
              {batches?.map((batch) => (
                <option key={batch.id} value={batch.id}>
                  {batch.name || `Batch ${batch.batch_number}`} ({batch.current_members} members)
                </option>
              ))}
            </select>
            {errors.batch_id && (
              <p className="mt-1 text-sm text-red-600">{errors.batch_id.message}</p>
            )}
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={() => {
                setIsStartModalOpen(false);
                reset();
              }}
              className="flex-1 btn-secondary"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={startMutation.isLoading}
              className="flex-1 btn-primary"
            >
              {startMutation.isLoading ? 'Starting...' : 'Start Draw'}
            </button>
          </div>
        </form>
      </Modal>

      {/* Confirm Action Dialog */}
      {confirmAction && (
        <ConfirmDialog
          isOpen={true}
          onClose={() => setConfirmAction(null)}
          onConfirm={confirmActionHandler}
          title={confirmAction.title}
          message={confirmAction.message}
          confirmLabel={confirmAction.title}
          variant={confirmAction.type === 'complete' ? 'primary' : 'danger'}
          isLoading={completeMutation.isLoading || cancelMutation.isLoading}
        />
      )}
    </div>
  );
}
